import React, { PropsWithChildren } from 'react'
import styled from 'styled-components'
import { Tooltip, Link } from '@sketch/components'
import { routes } from '@sketch/modules-common'
import { VersionLink } from '../../../../versioning'
import { SymbolMaster } from '../../../../inspector'
import { ReactComponent as LibraryIcon } from '@sketch/icons/link-library-17'

export const ExternalLinkIcon = styled(LibraryIcon)`
  width: 17px;
  height: 17px;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  flex: none;
  position: absolute;
  right: 12px;
  top: 16px;
  display: none;
`

export const SymbolUnavailable: React.FCC = ({ children }) => (
  <Tooltip
    placement="top"
    content="This Symbol belongs to a Library that is unavailable"
  >
    {children}
  </Tooltip>
)

const SymbolLinkContainer = styled.div`
  pointer-events: auto;
  width: 100%;
`

type FrameDetailInspectorSymbolLinkProps = PropsWithChildren<{
  symbolMaster: SymbolMaster | null
  frameUuid?: string
  shareIdentifier?: string
  isSymbolLink?: boolean
}>

export function FrameDetailInspectorSymbolLink({
  symbolMaster,
  shareIdentifier,
  frameUuid,
  children,
  isSymbolLink,
}: FrameDetailInspectorSymbolLinkProps) {
  // Data is not consistent for now, so we need to check that both exist
  // in order to link to a proper artboard (symbol masters are just arboards)
  if (!symbolMaster || !frameUuid || !shareIdentifier) {
    return <SymbolUnavailable>{children}</SymbolUnavailable>
  }

  // When linking to a foreign symbol we always link to the latest version,
  // but for internal symbols we want a versioned link
  return (
    <SymbolLinkContainer>
      {symbolMaster?.isForeign ? (
        <Link
          data-testid="inspector-symbol-link"
          target="_blank"
          to={
            routes.FRAME.create({
              shareID: shareIdentifier,
              frameUUID: frameUuid,
            }) + '#Inspect'
          }
        >
          {children}
        </Link>
      ) : (
        <VersionLink
          target="_blank"
          data-testid="inspector-symbol-link"
          to={{
            routeKey: 'FRAME',
            routeParams: {
              shareID: shareIdentifier,
              frameUUID: frameUuid,
            },
            hash: '#Inspect',
          }}
        >
          {children}
        </VersionLink>
      )}
      {isSymbolLink && (
        <Tooltip placement="top" content="View in Library">
          <ExternalLinkIcon />
        </Tooltip>
      )}
    </SymbolLinkContainer>
  )
}
